import { Stack, Typography } from '@mui/material';
import { InsightReportValue } from './InsightReportValue';
import { DocumentationInsight, EnvironmentInsight, PictureInsight } from '@retail/products/types';
import { useProductsTFunction } from '@retail/products/i18n';
import { useCallback } from 'react';

interface Props {
  values: DocumentationInsight | PictureInsight | EnvironmentInsight;
  activeColor: { bg: string; text: string };
  includeNotIncludedItemData: boolean;
}

export function InsightReportChips({ values, activeColor, includeNotIncludedItemData }: Props) {
  const t = useProductsTFunction();

  const sortedValues = Object.entries(values).sort(([, a], [, b]) => {
    return a === true && (b === false || b === null) ? -1 : a === false && b === null ? -1 : 1;
  });

  const filteredValues = includeNotIncludedItemData
    ? sortedValues
    : sortedValues.filter((value) => value[1] === true || !!value[1]);

  const getDetailField = useCallback(
    (field: string, value: any) => {
      switch (field) {
        case 'svanen':
          return value?.svanenCertificateId
            ? `${t('products.mgItem.environmentDocumentation.certificateId')}: ${
                value.svanenCertificateId
              }`
            : undefined;
        case 'euBlomman':
          return value?.euBlommanCertificateId
            ? `${t('products.mgItem.environmentDocumentation.certificateId')}: ${
                value.euBlommanCertificateId
              }`
            : undefined;
        case 'bvb':
          return value ? (
            <Stack gap={1}>
              <Typography variant="body3">
                {value.bvbAssessmentId &&
                  `${t('products.mgItem.environmentDocumentation.bvbAssessmentId')}: ${
                    value.bvbAssessmentId
                  }`}
              </Typography>
              <Typography variant="body3">
                {value.bvbAssessmentUrl &&
                  `${t('products.mgItem.environmentDocumentation.bvbAssessmentURL')}: ${
                    value.bvbAssessmentUrl
                  }`}
              </Typography>
              <Typography variant="body3">
                {value.bvbAssessmentTotal &&
                  `${t('products.mgItem.environmentDocumentation.bvbAssessmentTotal')}: ${
                    value.bvbAssessmentTotal
                  }`}
              </Typography>
              {value.bvbCertificateId && (
                <Typography variant="body3">
                  {`${t('products.mgItem.environmentDocumentation.bvbCertificateId')}: ${
                    value.bvbCertificateId
                  }`}
                </Typography>
              )}
            </Stack>
          ) : undefined;
        case 'sundahusAssessment':
          return value?.sundahusAssessment
            ? `${t('products.mgItem.environmentDocumentation.sundahusAssessment')}: ${
                value.sundahusAssessment
              }`
            : undefined;

        default:
          return '';
      }
    },
    [t]
  );

  return (
    <Stack flexDirection="row" flexWrap="wrap" gap={0.5}>
      {filteredValues.map(([field, value]) => {
        return (
          <InsightReportValue
            key={field}
            activeColor={activeColor}
            name={field}
            active={value}
            tooltipInfo={typeof value !== 'boolean' ? getDetailField(field, value) : undefined}
          />
        );
      })}
    </Stack>
  );
}
